<template>
  <article
    :class="{
      section: true,
      target: droptarget,
      empty: products.length === 0,
      collapsed: collapsed,
    }"
    :data-section-id="id"
  >
    <header>
      <div>
        <div v-stack-x:sm class="flex section-title">
          <div class="spec-section-handle">
            <SloIcon name="GripVertical" />
          </div>
          <h1>
            <SloEditableText
              v-if="!readonly"
              ref="title"
              :autofocus="shouldAutofocus"
              :placeholder="'Section Name' | t"
              @enter="handleEnter()"
              @blur="handleBlur()"
              v-model="sectionName"
            />
            <span v-else v-html="sectionName" />
          </h1>
          <p v-if="collapsed && products.length > 0">
            {{ products.length }} product{{ products.length === 1 ? '' : 's' }}
          </p>
          <p v-if="products.length === 0">{{ 'Empty' | t }}</p>
        </div>
        <div v-stack-x:2xs>
          <SloButton v-if="!readonly" fill="clear" @click="$emit('remove', section.id)" class="delete-section">
            <SloIcon name="Trash" />
          </SloButton>
          <SloButton
            class="collapse"
            v-if="products.length !== 0"
            fill="clear"
            @click="handleToggle()"
          >
            <SloIcon name="ChevronUp" />
          </SloButton>
        </div>
      </div>
    </header>
    <div
      v-if="!collapsed && products.length !== 0"
      :class="{
        products: true,
        drag: isDragging,
        empty: products.length === 0,
      }"
      @pointermove="resetPointer"
      @dragenter.stop
      @dragleave.stop
    >
      <draggable
        class="dropzone"
        v-model="products"
        @start="handleStart"
        @end="handleEnd"
        v-bind="dragOptions"
      >
        <SpecCard
          :readonly="readonly"
          :class="{
            ['card-active']: dropTo === index,
            ['card-no-hover']: isDragging,
            ['card-swap-target']: !isDragging && dragFrom === index,
            ghost: isDragging && dragFrom === index,
          }"
          v-for="(product, index) in products"
          :key="product.type + '-' + product.id"
          :section="section"
          :product="product"
          :project="project"
        />
      </draggable>
    </div>
    <div
      :class="{ 'drag-container': true, multiple: selectCount > 1 }"
      v-if="drag"
      :style="transform"
    >
      <SpecCard
        class="drag selected card-active"
        :product="dragProduct"
        :section="section"
        :project="project"
        :style="dragRect"
        :dragging="true"
      />
    </div>
  </article>
</template>

<script>
import draggable from 'vuedraggable';
import {mapGetters, mapActions} from 'vuex';
import t from '../../main/i18n';
import SpecCard from './SpecCard.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloEditableText from '../../../lib/components/SloEditableText.vue';

export default {
  name: 'SpecSection',
  props: {
    readonly: {type: Boolean, default: false},
    project: {type: Object, required: true},
    section: {type: Object, required: true},
    draggable: {type: Object, default: () => ({})}
  },
  components: {
    draggable,
    SpecCard,
    SloIcon,
    SloButton,
    SloEditableText
  },
  filters: {
    t
  },
  created() {
    window.addEventListener('drag', this.handleMove);

    let ghost = document.querySelector('#spec-drag-ghost');
    if (ghost) {
      this.ghost = ghost;

    } else {
      ghost = document.createElement('img');
      ghost.id = 'spec-drag-ghost';
      ghost.src = '/themes/sloan/img/transparent.png';
      ghost.style.setProperty('cursor', 'grabbing');
      this.ghost = document.documentElement.appendChild(ghost);
    }
  },
  mounted() {
    this.initialSectionName = this.sectionName;
  },
  watch: {
    drag(isDragging) {
      if (isDragging) {
        document.documentElement.style.setProperty('overflow', 'hidden');
        document.documentElement.style.setProperty('cursor', 'grabbing');
      } else {
        document.documentElement.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('cursor');
      }
    }
  },
  methods: {
    ...mapActions('designspec', ['updateProjectMeta', 'saveProject']),
    handleToggle() {
      if (this.readonly) {
        this.$data._collapsed = !this.$data._collapsed;
      } else {
        this.$emit('toggle', this.section.id);
      }
    },
    handleEnter() {
      this.$refs.title.blur();
    },
    handleBlur() {
      if (this.sectionName !== this.initialSectionName) {
        this.saveProject({id: this.project.id});
        this.initialSectionName = this.sectionName;
      }
    },
    setFocus() {
      this.$refs.title.setFocus();
    },
    setIsDragging(isDragging) {
      this.updateProjectMeta({
        id: this.project.id,
        isDragging,
        dragSection: this.id
      });
    },
    checkMove(event) {
      if (event.newIndex === this.products.length) {
        event.preventDefault();
        return false;
      }
      return true;
    },
    resetPointer() {
      if (this.dragFrom !== null) {
        this.dragFrom = null;
        this.dropTo = null;
      }
    },
    handleMove(event) {
      const x = event.clientX;
      const y = event.clientY;
      this.position = {x, y};
    },
    setData(dataTransfer) {
      if ('setDragImage' in dataTransfer) {
        dataTransfer.setDragImage(this.ghost, 0, 0);
      }
    },
    handleStart(event) {
      this.dragFrom = event.oldIndex;
      this.droptarget = true;
      this.offset = {
        x: event.originalEvent.offsetX,
        y: event.originalEvent.offsetY
      };
      this.rect = event.originalEvent.target.getBoundingClientRect();
      this.drag = true;
      this.setIsDragging(true);
      this.dragFrom = event.oldIndex;
    },
    handleEnd(event) {
      this.droptarget = false;
      this.setIsDragging(false);
      this.drag = false;
      this.dropTo = event.newIndex;
      this.position = {x: 0, y: 0};
      this.rect = null;
    }
  },
  computed: {
    ...mapGetters('designspec', {getProject: 'project'}),
    shouldAutofocus() {
      return (this.sectionName === t('New Section') || this.sectionName === '')
        && this.products.length === 0;
    },
    collapsed() {
      if (this.readonly) {
        return this.$data._collapsed;
      }
      return (this.section.meta || {}).collapsed;
    },
    selectCount() {
      return 0;
    },
    isDragging() {
      const meta = this.project.meta || {};
      return meta.isDragging && meta.dragSection === this.id;
    },
    checked() {
      return [];
    },
    dragProduct() {
      if (!this.drag) return null;
      return this.products[this.dragFrom];
    },
    dragRect() {
      if (!this.rect) return null;
      return {
        width: `${this.rect.width}px`,
        height: `${this.rect.height}px`,
        transform: 'scale(1.025)'
      };
    },
    transform() {
      const x = this.position.x - this.offset.x;
      const y = this.position.y - this.offset.y;
      const transform = `translate(${x}px, ${y}px)`;

      if (x < 0 && y < 0) return {transform, opacity: 0, transition: 'none'};

      return {
        transform,
        opacity: this.droptarget && this.drag ? 1 : 0
      };
    },
    dragOptions() {
      return {
        animation: 400,
        easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
        disabled: false,
        direction: 'horizontal',
        ghostClass: 'ghost',
        swapThreshold: 0.5,
        sort: false,
        multiDrag: false,
        group: {
          name: this.id + '-products',
          put: (to, from) => from.options.group.name === this.id + '-products',
          pull: (to, from) => from.options.group.name === this.id + '-products'
        },
        onChange: this.checkMove,
        emptyInsertThreshold: 500,
        setData: this.setData
      };
    },
    id() {
      return this.section.id;
    },
    sectionName: {
      get() {
        return this.section.name;
      },
      set(name) {
        this.$store.dispatch('designspec/updateSectionName', {
          project: this.project.id,
          section: this.section.id,
          name
        });
      }
    },
    products: {
      get() {
        return this.section.products;
      },
      set(products) {
        this.$store.dispatch('designspec/setProducts', {
          project: this.project.id,
          section: this.id,
          products
        });
      }
    }
  },
  data() {
    return {
      _collapsed: false,
      initialSectionName: t('New Section'),
      droptarget: false,
      rect: null,
      offset: {x: 0, y: 0},
      position: {x: 0, y: 0},
      ghost: null,
      dragFrom: null,
      dropTo: null,
      drag: false
    };
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.slo-card.card-swap-target,
.slo-card.card-no-hover:hover {
  background-color: #fafafa;

  &::before {
    opacity: 0;
  }

  .slo-card--overlay {
    display: none;
  }

  .slo-card--title {
    color: initial;
  }

  * {
    pointer-events: none;
  }
}

.slo-card.card-no-hover:hover {
  cursor: -moz-grabbing;
  cursor: -webkit-grabbing;
  cursor: grabbing;
}

.slo-card.spec-card.card-active {
  background-color: white;

  &::before {
    opacity: 1;
  }

  .slo-card--title {
    color: $color-blue-400;
  }
}

.msie .slo-card.spec-card.ghost {
  box-sizing: border-box !important;
  border: 5px solid transparent !important;
}

.msie .drag-container {
  display: none;
}

.designspec--project {
  .sections.collapse-all {
    .section {
      header {
        margin-bottom: 0 !important;
      }

      .slo-button.collapse .slo-icon {
        transform: translate(-50%, -50%) rotate(180deg);
      }

      .products {
        display: none;
      }
    }
  }
}
</style>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.flip-list-move {
  transition: transform 0.5s;
}

.v-enter-from {
  opacity: 0;
  transition: opacity 300ms cubic-bezier(0.215, 0.61, 0.355, 1);
}

.v-enter-to {
  opacity: 1;
}

.v-leave-from {
  opacity: 1;
  transition: opacity 300ms cubic-bezier(0.215, 0.61, 0.355, 1);
}

.v-leave-to {
  opacity: 1;
}

.flip-list-enter-active,
.flip-list-leave-active {
  transition: transform 200ms linear;
}

.flex {
  display: flex;
  align-items: center;
}

.drag-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 235px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 200ms cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 1090;

  .spec-card {
    margin: 0;
    border: 1px solid $color-blue-400;
    border-radius: 2px;

    &::before {
      opacity: 1;
    }

    cursor: grabbing !important;
  }
}

.section {
  background: white;
  border: 1px solid $color-gray-400;
  border-radius: 4px;
  padding: 8px 12px;

  @media screen and (min-width: $screen-sm-min) {
    padding: 16px 24px;
  }
}

.section.empty,
.section.collapsed {
  header {
    margin-bottom: 0 !important;
  }
}

.section .slo-button.collapse .slo-icon {
  transition: transform 180ms cubic-bezier(0.645, 0.045, 0.355, 1);
}

.section.collapsed .slo-button.collapse .slo-icon {
  transform: translate(-50%, -50%) rotate(180deg);
}

.section.target {
  border: 1px solid $color-blue-400;
  z-index: -1;

  .empty-card {
    pointer-events: none;
  }
}

.section-title {
  align-items: center;

  .spec-section-handle {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    position: relative;
    margin-right: -8px !important;
    cursor: grab;
    color: $color-gray-700;

    &:hover {
      color: $color-gray-900;
    }

    &:active {
      cursor: grabbing;
      background: $color-gray-100;
      color: $color-gray-900;
    }

    .slo-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  h1 {
    @include text('lg', 'strong');
    color: $color-gray-900;
  }

  p {
    @include text('sm');
    align-self: flex-end;
    margin-bottom: 4px !important;
    color: $color-gray-700;
    z-index: 1;
  }
}

header {
  display: flex;
  align-items: center;
  height: 32px;
  margin-bottom: 24px;

  > div {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .slo-button {
    position: relative;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    color: $color-gray-400;
    padding: 0;
    overflow: hidden;

    .slo-icon {
      position: absolute;
      top: 50%;
      left: 50%;

      width: 36px;
      height: 36px;
      transform: translate(-50%, -50%);
    }

    &:hover {
      color: $color-gray-700;
    }

    &:active {
      background: rgba($color-gray-400, 0.1);
      color: $color-gray-900;
    }
  }
}

.products > .dropzone {
  display: block;
  z-index: 1;

  @supports (display: grid) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 0.5fr));
    grid-auto-rows: minmax(1fr, 280px);
    gap: 10px;
  }
}

.msie .dropzone {
  display: flex;
  flex-flow: row wrap;
}

.msie .slo-card {
  box-sizing: border-box;
  border: 5px solid transparent;
  max-width: 231px;
}

.empty-card,
.spec-card {
  z-index: 1;
}
</style>
